// A wide floor of tiles that plays itself behind the sign-in card: several pieces fall at once, an
// automatic player steers each into the pile, and full rows clear. The cursor pushes pieces aside,
// and they find somewhere else to fit. Only the rules live here; FallingTiles.js draws it.

export const TICK_MS = 150;
export const CLEAR_TICKS = 6;

// How many pieces are in the air scales with the width of the floor
const SPAWN_SPACING = 220;
// Pieces start this many columns away from where they will land, so the steering shows
const MAX_WANDER = 6;
// On a floor this wide one stray gap would stop a row for good, so a nearly full row counts
const FULL_ROW = 0.94;
// Pieces keep this many cells clear of the cursor
const NUDGE_REACH = 3;
// How many columns further along a pushed piece looks for somewhere to fit
const NUDGE_RANGE = 10;

// The colours of the Tileward logo, grouped left to right across it: warm to cool
const COLOUR_BANDS = [
  ['#ce6653', '#d19348'],
  ['#d6a45d', '#dabf96'],
  ['#a1c0af', '#7fb895'],
  ['#6ca680', '#77b6a1', '#5a8f7b'],
];

// [column, row] of each tile. Pairs, threes, fives and sixes.
const SHAPES = [
  [[0, 0], [1, 0]],
  [[0, 0], [1, 0], [2, 0]],
  [[0, 0], [1, 0], [0, 1]],
  [[0, 0], [1, 0], [2, 0], [3, 0], [4, 0]],
  [[0, 0], [1, 0], [2, 0], [0, 1], [2, 1]],
  [[0, 0], [1, 0], [2, 0], [1, 1], [1, 2]],
  [[0, 0], [0, 1], [0, 2], [1, 2], [2, 2]],
  [[0, 0], [1, 0], [0, 1], [1, 1], [0, 2]],
  [[0, 0], [1, 0], [2, 0], [0, 1], [1, 1], [2, 1]],
];

// In a row on its way out, a gap that can no longer be filled; not drawn
export const GAP = '';

// The logo itself drops in now and then, in its own colours and never turned
const LOGO = {
  turns: [[[1, 0], [2, 0], [3, 0], [0, 1], [1, 1], [2, 1], [3, 1], [1, 2], [2, 2]]],
  colours: ['#dabf96', '#a1c0af', '#77b6a1', '#ce6653', '#d6a45d', '#7fb895', '#5a8f7b', '#d19348', '#6ca680'],
};
const LOGO_CHANCE = 0.03;

const width = (cells) => Math.max(...cells.map(([x]) => x)) + 1;
const height = (cells) => Math.max(...cells.map(([, y]) => y)) + 1;

const turned = (cells) => {
  const quarter = cells.map(([x, y]) => [-y, x]);
  const left = Math.min(...quarter.map(([x]) => x));
  const top = Math.min(...quarter.map(([, y]) => y));
  return quarter.map(([x, y]) => [x - left, y - top]).sort((a, b) => a[1] - b[1] || a[0] - b[0]);
};

// The distinct ways a shape can lie
const turnsOf = (cells) => {
  const quarters = [cells];
  for (let i = 1; i < 4; i += 1) quarters.push(turned(quarters[i - 1]));
  const seen = quarters.map((quarter) => JSON.stringify(quarter));
  return quarters.filter((_, i) => seen.indexOf(seen[i]) === i);
};

const PIECES = SHAPES.map((cells) => ({ turns: turnsOf(cells) }));

export const createBoard = (cols, rows, { rng = Math.random, maxPile = Math.ceil(rows * 0.3) } = {}) => ({
  cols,
  rows,
  rng,
  maxPile,
  // Colour of each settled tile, null where there is none
  grid: Array.from({ length: rows }, () => new Array(cols).fill(null)),
  // How far each row still has to settle after a clear, in rows
  settling: new Array(rows).fill(0),
  falling: [],
  // Rows on their way out: { row, age, erase }
  clearing: [],
  spawnEvery: Math.max(3, Math.round(SPAWN_SPACING / cols)),
  spawnIn: 0,
});

const fullRow = (board) => Math.ceil(board.cols * FULL_ROW);

const overlaps = (cells, x, y, other) => {
  const otherCells = other.shape.turns[other.turn];
  return cells.some(([cx, cy]) => otherCells.some(([ox, oy]) => cx + x === ox + other.x && cy + y === oy + other.y));
};

const fits = (board, piece, turn, x, y) => {
  const cells = piece.shape.turns[turn];
  const inBounds = cells.every(([cx, cy]) => {
    const col = cx + x;
    const row = cy + y;
    return col >= 0 && col < board.cols && row < board.rows && (row < 0 || board.grid[row][col] === null);
  });
  return inBounds && !board.falling.some((other) => other !== piece && overlaps(cells, x, y, other));
};

// The row of the piece's top when dropped straight down on columns whose highest tiles are at `tops`
const landing = (tops, cells, x) => Math.min(...cells.map(([cx, cy]) => tops[cx + x] - cy - 1));

const place = (tops, filled, cells, x, y) => {
  cells.forEach(([cx, cy]) => {
    tops[cx + x] = Math.min(tops[cx + x], cy + y);
    if (cy + y >= 0) filled[cy + y] += 1;
  });
};

// The top of each column and the tiles in each row, counting where pieces in the air will end up,
// apart from the one being placed
const surface = (board, placing) => {
  const tops = new Array(board.cols).fill(board.rows);
  const filled = new Array(board.rows).fill(0);
  board.grid.forEach((row, y) => row.forEach((colour, x) => {
    if (colour === null) return;
    tops[x] = Math.min(tops[x], y);
    filled[y] += 1;
  }));
  board.falling.filter((piece) => piece !== placing).sort((a, b) => b.y - a.y).forEach((piece) => {
    const cells = piece.shape.turns[piece.target.turn];
    place(tops, filled, cells, piece.target.x, landing(tops, cells, piece.target.x));
  });
  return { tops, filled };
};

// Higher is better: finish rows, stay low and level, leave no gaps underneath
const rate = (board, { tops, filled }, cells, x, y) => {
  const after = tops.slice(Math.max(0, x - 1), x + width(cells) + 1);
  const offset = Math.max(0, x - 1);
  let gaps = 0;
  let finished = 0;
  const lowest = new Map();
  const perRow = new Map();
  cells.forEach(([cx, cy]) => {
    const col = cx + x;
    lowest.set(col, Math.max(lowest.get(col) ?? -1, cy + y));
    perRow.set(cy + y, (perRow.get(cy + y) ?? 0) + 1);
    after[col - offset] = Math.min(after[col - offset], cy + y);
  });
  lowest.forEach((row, col) => { gaps += tops[col] - row - 1; });
  perRow.forEach((count, row) => {
    if (row >= 0 && filled[row] + count >= fullRow(board)) finished += 1;
  });
  let steps = 0;
  for (let i = 1; i < after.length; i += 1) steps += Math.abs(after[i] - after[i - 1]);
  return finished * 14 + y * 2.5 - gaps * 7 - steps + board.rng() * 1.5;
};

// The best place for a shape, with its left edge between columns `from` and `to`
const choosePlacement = (board, shape, { from = 0, to = board.cols, piece = null } = {}) => {
  const ground = surface(board, piece);
  let best = null;
  shape.turns.forEach((cells, turn) => {
    for (let x = Math.max(0, from); x <= Math.min(to, board.cols - width(cells)); x += 1) {
      const y = landing(ground.tops, cells, x);
      const score = y >= 0 ? rate(board, ground, cells, x, y) : -Infinity;
      if (!best || score > best.score) best = { turn, x, score };
    }
  });
  return best;
};

const randomFrom = (board, items) => items[Math.floor(board.rng() * items.length)];

// Warm on the left of the floor, cool on the right, like the logo
const colourFor = (board, col) => {
  const position = (col / board.cols) * COLOUR_BANDS.length + (board.rng() - 0.5) * 1.4;
  const band = Math.min(COLOUR_BANDS.length - 1, Math.max(0, Math.floor(position)));
  return randomFrom(board, COLOUR_BANDS[band]);
};

const spawn = (board) => {
  board.spawnIn -= 1;
  if (board.spawnIn > 0) return;
  board.spawnIn = 1;
  const shape = board.rng() < LOGO_CHANCE ? LOGO : randomFrom(board, PIECES);
  const target = choosePlacement(board, shape);
  const turn = Math.floor(board.rng() * shape.turns.length);
  const cells = shape.turns[turn];
  const wander = Math.round((board.rng() * 2 - 1) * MAX_WANDER);
  const x = Math.max(0, Math.min(board.cols - width(cells), target.x + wander));
  const y = -height(cells) - 1;
  const colour = colourFor(board, target.x);
  const piece = { shape, turn, x, y, fromY: y, spun: false, target, colour };
  if (!fits(board, piece, turn, x, y)) return;
  board.falling.push(piece);
  board.spawnIn = board.spawnEvery;
};

// The colour of tile `index` of a piece
export const tileColour = (piece, index) => piece.shape.colours?.[index] ?? piece.colour;

const lock = (board, piece) => {
  piece.shape.turns[piece.turn].forEach(([cx, cy], index) => {
    if (cy + piece.y >= 0) board.grid[cy + piece.y][cx + piece.x] = tileColour(piece, index);
  });
};

const resting = (board, piece) => piece.shape.turns[piece.turn].some(([cx, cy]) => {
  const row = cy + piece.y + 1;
  return row >= board.rows || (row >= 0 && board.grid[row][cx + piece.x] !== null);
});

// One tick of the automatic player: turn, then steer, then fall. True when the piece has landed.
const advance = (board, piece) => {
  piece.fromY = piece.y;
  piece.spun = false;
  const { turns } = piece.shape;
  const nextTurn = (piece.turn + 1) % turns.length;
  if (piece.turn !== piece.target.turn && fits(board, piece, nextTurn, piece.x, piece.y)) {
    piece.turn = nextTurn;
    piece.spun = true;
  } else if (piece.turn === piece.target.turn && piece.x !== piece.target.x) {
    const x = piece.x + Math.sign(piece.target.x - piece.x);
    if (fits(board, piece, piece.turn, x, piece.y)) piece.x = x;
  }
  if (resting(board, piece)) {
    lock(board, piece);
    return true;
  }
  // Another piece in the way only holds this one up for a moment
  if (fits(board, piece, piece.turn, piece.x, piece.y + 1)) piece.y += 1;
  return false;
};

// A removed row brings everything above it down one, the pieces in the air included
const removeRow = (board, row) => {
  board.grid.splice(row, 1);
  board.grid.unshift(new Array(board.cols).fill(null));
  board.settling.splice(row, 1);
  board.settling.unshift(0);
  for (let y = 0; y <= row; y += 1) board.settling[y] += 1;
  board.clearing.forEach((entry) => {
    if (entry.row < row) entry.row += 1;
  });
  board.falling.forEach((piece) => {
    if (piece.y + height(piece.shape.turns[piece.turn]) <= row) {
      piece.y += 1;
      piece.fromY += 1;
    }
  });
};

const finishClears = (board) => {
  board.clearing.forEach((entry) => { entry.age += 1; });
  const done = board.clearing.filter((entry) => entry.age >= CLEAR_TICKS).sort((a, b) => a.row - b.row);
  board.clearing = board.clearing.filter((entry) => entry.age < CLEAR_TICKS);
  done.forEach((entry) => {
    if (entry.erase) board.grid[entry.row].fill(null);
    else removeRow(board, entry.row);
  });
};

const markRows = (board) => {
  const leaving = new Set(board.clearing.map((entry) => entry.row));
  const ceiling = board.rows - board.maxPile;
  // A row with a piece in one of its gaps waits for the piece to settle
  const occupied = new Set(board.falling.flatMap((piece) => piece.shape.turns[piece.turn].map(([, cy]) => cy + piece.y)));
  board.grid.forEach((cells, row) => {
    if (leaving.has(row)) return;
    const tiles = cells.filter((colour) => colour !== null).length;
    if (tiles >= fullRow(board) && !occupied.has(row)) {
      // The gaps are closed until it goes, so nothing can fall into them
      board.grid[row] = cells.map((colour) => colour ?? GAP);
      board.clearing.push({ row, age: 0, erase: false });
    } else if (row < ceiling && tiles > 0) {
      // A pile that has grown too tall loses its top, without anything moving
      board.clearing.push({ row, age: 0, erase: true });
    }
  });
};

export const step = (board) => {
  board.settling.forEach((left, y) => { board.settling[y] = Math.max(0, left - 1 / 3); });
  finishClears(board);
  spawn(board);
  // Lowest first, so each piece moves out of the way of the one above
  board.falling.sort((a, b) => b.y - a.y);
  board.falling = board.falling.filter((piece) => !advance(board, piece));
  markRows(board);
};

// How many cells lie between a point and the nearest tile of a piece
const gapTo = (piece, { col, row }) => Math.min(...piece.shape.turns[piece.turn].map(([cx, cy]) => {
  const across = Math.max(cx + piece.x - col, col - (cx + piece.x + 1), 0);
  const down = Math.max(cy + piece.y - row, row - (cy + piece.y + 1), 0);
  return Math.hypot(across, down);
}));

// The cursor, in cells, pushes nearby pieces aside: each moves a column away from it, and looks for
// somewhere to fit on that side instead of steering back. The pile stays put.
export const nudge = (board, pointer) => {
  board.falling.forEach((piece) => {
    if (gapTo(piece, pointer) >= NUDGE_REACH) return;
    const side = piece.x + width(piece.shape.turns[piece.turn]) / 2 >= pointer.col ? 1 : -1;
    const x = piece.x + side;
    if (!fits(board, piece, piece.turn, x, piece.y)) return;
    piece.x = x;
    const range = side > 0 ? { from: x, to: x + NUDGE_RANGE } : { from: x - NUDGE_RANGE, to: x };
    piece.target = choosePlacement(board, piece.shape, { ...range, piece }) ?? piece.target;
  });
};

// Moves the board on without showing it, so the page opens on a game already under way
export const warmUp = (board, ticks) => {
  for (let i = 0; i < ticks; i += 1) step(board);
};
